/**
 * Types for flashcard decks and items
 */

export type Language =
  | 'ENGLISH'
  | 'CHINESE'
  | 'HINDI'
  | 'SPANISH'
  | 'FRENCH'
  | 'ARABIC'
  | 'BENGALI'
  | 'PORTUGUESE'
  | 'RUSSIAN'
  | 'URDU'
  | 'INDONESIAN'
  | 'GERMAN'
  | 'JAPANESE'
  | 'TURKISH'
  | 'TAMIL'
  | 'VIETNAMESE'
  | 'KOREAN'
  | 'MARATHI'
  | 'TELUGU'
  | 'ITALIAN';

export type DeckVisibility = 'PUBLIC' | 'UNLISTED' | 'PRIVATE';

export type DeckStatus = 'DRAFT' | 'PUBLISHED' | 'ARCHIVED';

export type DeckAccess =
  | 'PUBLIC'
  | 'MEMBER_PUBLIC'
  | 'REQUIRE_PAYMENT'
  | 'PASSWORD_PROTECTED'
  | 'UNLISTED'
  | 'PRIVATE';

export type Difficulty = 'BEGINNER' | 'INTERMEDIATE' | 'ADVANCED';

export interface EmbeddedContentItem {
  id?: string;
  type: string;
  title?: string;
  content?: string;
  metadata?: Record<string, unknown>;
  // ReferencedContentItem blocks (PRACTICE, FLASHCARD_DECK, AUDIO/IMAGE/...): id of the
  // external entity (quiz/deck/media) this block points at - content is not embedded inline.
  contentId?: string;
  description?: string;
  text?: string;
  // AUDIO/IMAGE/HOSTED_VIDEO blocks: presigned URL resolved fresh server-side on every read,
  // not part of `content` (which is always empty for these reference-only block types).
  previewUrl?: string;
  mimeType?: string;
}

export interface FlashCardSide {
  title?: string;
  content?: EmbeddedContentItem[];
}

export interface FlashCardItem {
  id?: string;
  createdDate?: string;
  updatedDate?: string;
  authorId?: string;
  deckId?: string;
  sides: FlashCardSide[];
}

export interface FlashCardDeck {
  id?: string;
  createdDate?: string;
  updatedDate?: string;
  authorId?: string;
  title?: string;
  description?: string;
  subject?: string;
  source?: Language;
  target?: Language;
  difficulty?: Difficulty;
  visibility?: DeckVisibility;
  status?: DeckStatus;
  access?: DeckAccess;
  appId?: string;
  system?: boolean;
}

export interface FlashCardDeckDTO {
  id: string;
  title: string;
  description?: string;
  subject?: string;
  source?: Language;
  target?: Language;
  difficulty?: Difficulty;
  visibility?: DeckVisibility;
  status?: DeckStatus;
  access?: DeckAccess;
  appId?: string;
  authorId?: string;
  createdDate?: string;
  updatedDate?: string;
  itemsCount?: number;
  system?: boolean;
}

export interface PageableResponse<T> {
  totalPages: number;
  totalItems: number;
  pageIndex: number;
  pageSize: number;
  data: T[];
}

export type FlashCardDecksResponse = PageableResponse<FlashCardDeckDTO>;
export type FlashCardItemsResponse = PageableResponse<FlashCardItem>;

export interface CreateFlashCardsRequest {
  cards: FlashCardInput[];
}

export interface FlashCardInput {
  sides: FlashCardSide[];
}

export interface UpdateFlashCardRequest {
  sides: FlashCardSide[];
}

export interface FlashCardDeckInput {
  title: string;
  description?: string;
  subject?: string;
  source?: Language;
  target?: Language;
  difficulty?: Difficulty;
  visibility?: DeckVisibility;
  status?: DeckStatus;
  access?: DeckAccess;
}

export interface GenerateFlashCardDeckRequest {
  source: Language;
  target: Language;
  topic: string;
  count: number;
  difficulty: Difficulty;
}

export interface GeneratedFlashCardDeckResponse {
  id: string;
  title?: string;
  description?: string;
  subject?: string;
  source?: Language;
  target?: Language;
  visibility?: DeckVisibility;
  status?: DeckStatus;
  access?: DeckAccess;
  appId?: string;
  items?: FlashCardItemResponse[];
  system?: boolean;
}

export interface FlashCardItemResponse {
  id: string;
  deckId?: string;
  authorId?: string;
  sides?: FlashCardSideResponse[];
}

export interface FlashCardSideResponse {
  title?: string;
  content?: ContentItemResponse[];
}

export interface ContentItemResponse {
  id?: string;
  content?: string;
  type?: string;
  title?: string;
  metadata?: Record<string, unknown>;
}

export const LANGUAGES: { value: Language; label: string }[] = [
  { value: 'ENGLISH', label: 'English' },
  { value: 'CHINESE', label: 'Chinese' },
  { value: 'HINDI', label: 'Hindi' },
  { value: 'SPANISH', label: 'Spanish' },
  { value: 'FRENCH', label: 'French' },
  { value: 'ARABIC', label: 'Arabic' },
  { value: 'BENGALI', label: 'Bengali' },
  { value: 'PORTUGUESE', label: 'Portuguese' },
  { value: 'RUSSIAN', label: 'Russian' },
  { value: 'URDU', label: 'Urdu' },
  { value: 'INDONESIAN', label: 'Indonesian' },
  { value: 'GERMAN', label: 'German' },
  { value: 'JAPANESE', label: 'Japanese' },
  { value: 'TURKISH', label: 'Turkish' },
  { value: 'TAMIL', label: 'Tamil' },
  { value: 'VIETNAMESE', label: 'Vietnamese' },
  { value: 'KOREAN', label: 'Korean' },
  { value: 'MARATHI', label: 'Marathi' },
  { value: 'TELUGU', label: 'Telugu' },
  { value: 'ITALIAN', label: 'Italian' }
];
